import { InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import type { PeriodicGroupsQuery } from '../../../graphql/generated/graphql.ts';
import { translatePeriod } from '../../../utils/enumHelpers';
import { PERIODIC_GROUPS_QUERY } from './PeriodicGroups.gql';
import { CreatePeriodicGroupModal } from './periodicGroups/CreatePeriodicGroupModal.tsx';
import { UpdatePeriodicGroupModal } from './periodicGroups/UpdatePeriodicGroupModal.tsx';

const { useToken } = theme;

export type PeriodicGroup = NonNullable<PeriodicGroupsQuery['labPeriodicGroups']>[number];

export const PeriodicGroups: React.FC = () => {
  const { token } = useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editGroup, setEditGroup] = useState<PeriodicGroup | null>(null);

  const { data, loading, refetch } = useQuery(PERIODIC_GROUPS_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<PeriodicGroup> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Max. Anzahl',
      dataIndex: 'maxCount',
      key: 'maxCount',
      ellipsis: true,
      sorter: (a, b) => (a.maxCount === b.maxCount ? 0 : a.maxCount > b.maxCount ? 1 : -1),
    },
    {
      title: 'Zeitraum',
      dataIndex: 'period',
      key: 'period',
      ellipsis: true,
      sorter: (a, b) => a.period.localeCompare(b.period),
      render: value => translatePeriod(value),
    },
  ];

  const filteredGroups: PeriodicGroup[] = useMemo(
    () => filterByTokens(data?.labPeriodicGroups ?? [], debouncedSearch, [g => g.name]),
    [debouncedSearch, data]
  );

  return (
    <MainContent>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          allowClear
          autoFocus
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Name">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalVisible(true)}>
          Neue periodische Verrechnungsgruppe
        </Button>
      </div>
      <Table<PeriodicGroup>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredGroups}
        onRow={record => ({ onClick: () => setEditGroup(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} periodischen Verrechnungsgruppen`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreatePeriodicGroupModal
        modalVisible={createModalVisible}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalVisible(false)}
        labId={labId ?? ''}
      />
      <UpdatePeriodicGroupModal group={editGroup} onSuccess={() => refetch()} onClose={() => setEditGroup(null)} />
    </MainContent>
  );
};
